import { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styled, { css, keyframes } from 'styled-components';

import { ArrowDown } from '@/assets/icons/info-icons/arrowDown';
import { themeVar } from '@/styles/theme';

export interface IActionDropdownOption {
  name: string;
  onClick: () => void;
}

const actionDropdownSizes = {
  sm: css`
    width: 32px;
    height: 32px;
  `,
  md: css`
    width: 40px;
    height: 40px;
  `,
  lg: css`
    width: 50px;
    height: 50px;
  `
};

const actionDropdownItemSizes = {
  sm: css`
    font-size: 13px;
    font-weight: 600;
    line-height: 18.2px;
    padding: 5px 8px;
    width: 204px;
  `,
  md: css`
    font-size: 16px;
    font-weight: 600;
    line-height: 22.4px;
    padding: 10px;
    width: 285px;
  `,
  lg: css`
    font-size: 19px;
    font-weight: 600;
    line-height: 26.6px;
    padding: 10px 12px;
    width: 310px;
  `
};

const actionDropdownBorderRadiuses = {
  sm: css`
    border-radius: 3px;
  `,
  md: css`
    border-radius: 4px;
  `,
  lg: css`
    border-radius: 5px;
  `
};

const actionDropdownStyleTypes = {
  default: css`
    background-color: ${themeVar('action.default')};
    border: 1px solid ${themeVar('action.default')};
  `,
  outline: css`
    background-color: ${themeVar('layer.base')};
    border: 1px solid ${themeVar('action.default')};

    svg {
      path {
        fill: ${themeVar('action.default')};
      }
    }

    &:hover {
      background-color: ${themeVar('action.default')};
      border: 1px solid ${themeVar('action.default')};

      svg {
        path {
          fill: ${themeVar('context.light')};
        }
      }
    }
  `,
  ghost: css`
    background-color: ${themeVar('layer.base')};
    border: 1px solid ${themeVar('layer.base')};

    svg {
      path {
        fill: ${themeVar('action.default')};
      }
    }

    &:hover {
      background-color: ${themeVar('action.default')};
      border: 1px solid ${themeVar('action.default')};

      svg {
        path {
          fill: ${themeVar('context.light')};
        }
      }
    }
  `,
  transparent: css`
    background-color: transparent;

    svg {
      path {
        fill: ${themeVar('action.default')};
      }
    }

    &:hover {
      background-color: ${themeVar('action.default')};
      border: 1px solid ${themeVar('action.default')};

      svg {
        path {
          fill: ${themeVar('context.light')};
        }
      }
    }
  `,
  disabled: css`
    background-color: ${themeVar('action.disabled')};

    svg {
      path {
        fill: ${themeVar('font.disabled')};
      }
    }
  `,
  active: css`
    background-color: ${themeVar('menu.active')};
  `
};

interface ActionDropdownProps {
  options: IActionDropdownOption[];
  size?: 'sm' | 'md' | 'lg';
  styleType?: 'default' | 'outline' | 'ghost' | 'transparent' | 'disabled' | 'active';
  actionDropdownListPosition?: 'right' | 'left' | 'center';
  className?: string;
}

const ActionDropdown = ({ options, size = 'md', styleType = 'default', actionDropdownListPosition = 'right', className }: ActionDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const ref = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const handleActionDropdown = () => {
    if (styleType === 'disabled') return;
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    const handleCloseDropdown = (e: MouseEvent) => {
      if (isOpen && ref.current && listRef.current) {
        const isClickInsideButton = ref.current.contains(e.target as Node);
        const isClickInsideList = listRef.current.contains(e.target as Node);

        if (!isClickInsideButton && !isClickInsideList) {
          setIsOpen(false);
        }
      }
    };

    const handleResize = () => {
      if (isOpen) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleCloseDropdown);
      window.addEventListener('resize', handleResize);
    }

    return () => {
      document.removeEventListener('mousedown', handleCloseDropdown);
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && ref.current && listRef.current) {
      const wrapperRect = ref.current.getBoundingClientRect();
      const listRect = listRef.current.getBoundingClientRect();

      const scrollY = window.scrollY;
      const scrollX = window.scrollX;

      const viewportWidth = window.innerWidth;
      const listWidth = listRect.width;

      let left = wrapperRect.left + scrollX;

      if (actionDropdownListPosition === 'right') {
        left = wrapperRect.left + scrollX - listWidth + wrapperRect.width;
      } else if (actionDropdownListPosition === 'left') {
        left = wrapperRect.left + scrollX;
      } else if (actionDropdownListPosition === 'center') {
        left = wrapperRect.left + scrollX + wrapperRect.width / 2 - listWidth / 2;
      }

      if (left + listWidth > viewportWidth) {
        left = viewportWidth - listWidth - 8;
      }

      if (left < 8) {
        left = 8;
      }

      const top = wrapperRect.bottom + scrollY + 8;

      setCoords({ top, left });
    }
  }, [isOpen, actionDropdownListPosition]);

  return (
    <>
      <ActionDropdownWrapper
        id="actionDropdown"
        onClick={handleActionDropdown}
        size={size}
        styleType={isOpen ? 'active' : styleType}
        ref={ref}
        isOpen={isOpen}
        className={className}
      >
        <ArrowDown fill={themeVar('context.light')} />
      </ActionDropdownWrapper>
      {isOpen &&
        ReactDOM.createPortal(
          <ActionDropdownListWrapper
            ref={listRef}
            size={size}
            actionDropdownListPosition={actionDropdownListPosition}
            style={{
              top: coords.top,
              left: coords.left
            }}
          >
            {options.map(({ name, onClick }) => (
              <ActionDropdownItem
                key={name}
                onClick={() => {
                  onClick();
                  setIsOpen(false);
                }}
                size={size}
              >
                {name}
              </ActionDropdownItem>
            ))}
          </ActionDropdownListWrapper>,
          document.body
        )}
    </>
  );
};

export default ActionDropdown;

const opacityAnimation = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const ActionDropdownWrapper = styled.div<{ size: ActionDropdownProps['size']; styleType: ActionDropdownProps['styleType']; isOpen: boolean }>`
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  transition: 0.1s ease;
  pointer-events: ${({ styleType }) => (styleType === 'disabled' ? 'none' : 'auto')};

  ${({ size }) => actionDropdownSizes[size]}
  ${({ size }) => actionDropdownBorderRadiuses[size]}
  ${({ styleType }) => actionDropdownStyleTypes[styleType]}
  ${({ isOpen }) =>
    isOpen &&
    css`
      opacity: 1 !important;
    `}
`;

const ActionDropdownListWrapper = styled.div<{
  size: ActionDropdownProps['size'];
  actionDropdownListPosition: ActionDropdownProps['actionDropdownListPosition'];
}>`
  position: absolute;
  z-index: 9999;
  cursor: default;
  display: flex;
  flex-direction: column;
  background: ${themeVar('layer.1')};
  border: 1px solid ${themeVar('border.base')};
  box-shadow: 0 4px 4px rgba(0, 0, 0, 0.15);
  padding: 10px 0;
  animation: ${opacityAnimation} 300ms;

  ${({ size }) => actionDropdownBorderRadiuses[size]}
`;

const ActionDropdownItem = styled.div<{ size: ActionDropdownProps['size'] }>`
  display: flex;
  cursor: pointer;
  text-transform: capitalize;
  text-align: left;
  color: ${themeVar('font.action')};
  background: ${themeVar('layer.1')};

  &:hover {
    color: ${themeVar('layer.base')};
    background: ${themeVar('font.action')};
  }

  ${({ size }) => actionDropdownItemSizes[size]}
`;
